import React, { useState, useEffect } from 'react';
import { OrderDetailsDTO, Priority } from '../../types/order';
import { API_BASE } from '../../config/api';
import { X } from 'lucide-react';

interface EditOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderDetailsDTO;
  onUpdate: () => Promise<void>;
}

const garmentTypes = [
  { id: 'suit', name: 'Suit' },
  { id: 'dress', name: 'Dress' },
  { id: 'shirt', name: 'Shirt' },
  { id: 'pants', name: 'Pants' },
  { id: 'blazer', name: 'Blazer' },
  { id: 'kurta', name: 'Kurta' },
  { id: 'sherwani', name: 'Sherwani' },
  { id: 'lehenga', name: 'Lehenga' },
  { id: 'saree_blouse', name: 'Saree Blouse' },
  { id: 'waistcoat', name: 'Waistcoat' },
  { id: 'trouser', name: 'Trouser' },
  { id: 'jacket', name: 'Jacket' },
  { id: 'other', name: 'Other' },
];

const EditOrderModal: React.FC<EditOrderModalProps> = ({
  isOpen,
  onClose,
  order,
  onUpdate,
}) => {
  const [formData, setFormData] = useState({
    description: order.notes?.customerNote || '',
    dueDate: order.dueAt ? new Date(order.dueAt).toISOString().split('T')[0] : '',
    priority: order.priority || 'MEDIUM',
    orderType: order.items?.[0]?.garmentType || '',
    fabric: order.items?.[0]?.fabric || '',
    quantity: order.items?.[0]?.quantity || 1,
    price: {
      materials: order.price?.materials || 0,
      labor: order.price?.labor || 0,
      discount: order.price?.discount || 0,
      total: order.price?.total || 0,
      currency: order.price?.currency || 'INR',
    },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        description: order.notes?.customerNote || '',
        dueDate: order.dueAt ? new Date(order.dueAt).toISOString().split('T')[0] : '',
        priority: order.priority || 'MEDIUM',
        orderType: order.items?.[0]?.garmentType || '',
        fabric: order.items?.[0]?.fabric || '',
        quantity: order.items?.[0]?.quantity || 1,
        price: {
          materials: order.price?.materials || 0,
          labor: order.price?.labor || 0,
          discount: order.price?.discount || 0,
          total: order.price?.total || 0,
          currency: order.price?.currency || 'INR',
        },
      });
      setError(null);
    }
  }, [isOpen, order]);

  const calculateTotal = () => {
    const materials = parseFloat(String(formData.price.materials)) || 0;
    const labor = parseFloat(String(formData.price.labor)) || 0;
    const discount = parseFloat(String(formData.price.discount)) || 0;
    return materials + labor - discount;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Build update payload
      const updatePayload: any = {};
      
      // Update notes/description
      if (formData.description !== (order.notes?.customerNote || '')) {
        updatePayload.notes = formData.description;
      }
      
      // Update due date
      if (formData.dueDate) {
        // Use 'due_date' for PUT endpoint, 'dueAt' for PATCH endpoint
        if (needsOrderTypeUpdate) {
          updatePayload.due_date = formData.dueDate;
        } else {
          updatePayload.dueAt = formData.dueDate;
        }
      }
      
      // Update priority
      if (formData.priority !== order.priority) {
        updatePayload.priority = formData.priority;
      }

      // Update order type (garment type)
      const currentOrderType = order.items?.[0]?.garmentType || '';
      if (formData.orderType !== currentOrderType) {
        updatePayload.order_type = formData.orderType;
      }

      // Update price
      const currentPrice = order.price || {};
      const priceChanged = 
        formData.price.materials !== (currentPrice.materials || 0) ||
        formData.price.labor !== (currentPrice.labor || 0) ||
        formData.price.discount !== (currentPrice.discount || 0) ||
        formData.price.total !== (currentPrice.total || 0);

      if (priceChanged) {
        updatePayload.price = {
          materials: formData.price.materials,
          labor: formData.price.labor,
          discount: formData.price.discount,
          total: formData.price.total || calculateTotal(),
          currency: formData.price.currency,
        };
      }

      // Use PUT for order_type update, PATCH for other fields
      // If order_type is being updated, use PUT endpoint which supports order_type
      const needsOrderTypeUpdate = formData.orderType !== currentOrderType;
      const method = needsOrderTypeUpdate ? 'PUT' : 'PATCH';
      
      // For PUT, we need to include order_type in the payload
      if (needsOrderTypeUpdate) {
        updatePayload.order_type = formData.orderType;
        // Also update notes to include fabric info
        if (formData.fabric) {
          const currentNotes = formData.description || '';
          const fabricNote = `Fabric/Cloth: ${formData.fabric}`;
          if (!currentNotes.includes('Fabric/Cloth:')) {
            updatePayload.notes = currentNotes ? `${currentNotes}\n${fabricNote}` : fabricNote;
          } else {
            // Replace existing fabric note
            updatePayload.notes = currentNotes.replace(
              /Fabric\/Cloth:\s*[^\n]+/i,
              fabricNote
            );
          }
        }
      }
      
      const endpoint = `${API_BASE}/api/orders/${order.id}`;

      if (Object.keys(updatePayload).length === 0) {
        onClose();
        return;
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update order');
      }

      await onUpdate();
      onClose();
    } catch (_err) {
      setError(err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 id="edit-modal-title" className="text-xl font-semibold text-gray-900">
              Edit Order #{order.id}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order Type / Garment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Type / Garment Type *
              </label>
              <select
                value={formData.orderType}
                onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select garment type...</option>
                {garmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Item Details */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Item Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Fabric */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fabric / Cloth Type
                  </label>
                  <input
                    type="text"
                    value={formData.fabric}
                    onChange={(e) => setFormData({ ...formData, fabric: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Cotton, Silk, etc."
                  />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Price Section */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Pricing</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Materials */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Materials Cost (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price.materials}
                    onChange={(e) => {
                      const materials = parseFloat(e.target.value) || 0;
                      setFormData({
                        ...formData,
                        price: { 
                          ...formData.price, 
                          materials,
                          total: materials + formData.price.labor - formData.price.discount
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Labor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Labor Cost (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price.labor}
                    onChange={(e) => {
                      const labor = parseFloat(e.target.value) || 0;
                      setFormData({
                        ...formData,
                        price: { 
                          ...formData.price, 
                          labor,
                          total: formData.price.materials + labor - formData.price.discount
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Discount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price.discount}
                    onChange={(e) => {
                      const discount = parseFloat(e.target.value) || 0;
                      setFormData({
                        ...formData,
                        price: { 
                          ...formData.price, 
                          discount,
                          total: formData.price.materials + formData.price.labor - discount
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Total */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price.total || calculateTotal()}
                    onChange={(e) => setFormData({
                      ...formData,
                      price: { ...formData.price, total: parseFloat(e.target.value) || 0 }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-calculated: Materials + Labor - Discount
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description / Notes
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter order description or notes..."
              />
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
